import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DataSaverOnRoundedIcon from "@mui/icons-material/DataSaverOnRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import VolumeMuteRoundedIcon from "@mui/icons-material/VolumeMuteRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import controls, {setAgentCallAudioBitrateKbps} from "./controls";
import {
  DATA_SAVER_BITRATE_KBPS,
  HIGH_FIDELITY_BITRATE_KBPS,
} from "./callAudioQuality";
import {auth, updateAgentActivityState} from "./firebase";
import {showSnackbar} from "./Snackbar";

const MIN_RESTORE_VOLUME = 5;
const TEMPORARY_MUTE_MS = 30000;
const VOLUME_STORAGE_KEY = "frecall-agent-waiting-volume";

const clampVolume = (value) => Math.min(100, Math.max(0, Number(value) || 0));

const readSavedVolume = () => {
  const stored = window.localStorage.getItem(VOLUME_STORAGE_KEY);
  if (stored === null || stored === "") return clampVolume(controls.volume);
  const saved = Number(stored);
  return Number.isFinite(saved) ? clampVolume(saved) : clampVolume(controls.volume);
};

const applyPlayerVolume = (value) => {
  controls.volume = value;
  const player = document.getElementById("myPlayer");
  if (player) player.volume = value / 100;
};

const VolumeIcon = ({value}) => {
  if (value <= 0) return <VolumeOffRoundedIcon />;
  if (value <= 25) return <VolumeMuteRoundedIcon />;
  if (value <= 65) return <VolumeDownRoundedIcon />;
  return <VolumeUpRoundedIcon />;
};

const AudioControls = ({themeMode, toggleThemeMode, callActive = false}) => {
  const theme = useTheme();
  const compactLayout = useMediaQuery(theme.breakpoints.down("md"));
  const [value, setValue] = React.useState(readSavedVolume);
  const [muted, setMuted] = React.useState(false);
  const [signOutOpen, setSignOutOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [draftAudioBitrate, setDraftAudioBitrate] = React.useState(
      controls.callAudioBitrateKbps,
  );
  const previousVolumeRef = React.useRef(Math.max(readSavedVolume(), MIN_RESTORE_VOLUME));
  const muteTimerRef = React.useRef(null);

  const clearMuteTimer = React.useCallback(() => {
    if (muteTimerRef.current) window.clearTimeout(muteTimerRef.current);
    muteTimerRef.current = null;
  }, []);

  const commitVolume = React.useCallback((nextValue, persist = true) => {
    const safeValue = clampVolume(nextValue);
    setValue(safeValue);
    applyPlayerVolume(safeValue);
    if (persist && safeValue > 0) {
      previousVolumeRef.current = safeValue;
      window.localStorage.setItem(VOLUME_STORAGE_KEY, String(safeValue));
    }
  }, []);

  React.useEffect(() => {
    applyPlayerVolume(value);
  }, [value]);

  React.useEffect(() => clearMuteTimer, [clearMuteTimer]);

  const restoreVolume = React.useCallback(() => {
    clearMuteTimer();
    setMuted(false);
    commitVolume(Math.max(previousVolumeRef.current, MIN_RESTORE_VOLUME), false);
  }, [clearMuteTimer, commitVolume]);

  const handleChange = (_, nextValue) => {
    clearMuteTimer();
    setMuted(nextValue === 0);
    commitVolume(nextValue);
  };

  const handleMuteToggle = () => {
    if (muted || value === 0) {
      restoreVolume();
      showSnackbar("Waiting music restored.");
      return;
    }

    previousVolumeRef.current = Math.max(value, MIN_RESTORE_VOLUME);
    clearMuteTimer();
    setMuted(true);
    commitVolume(0, false);
    muteTimerRef.current = window.setTimeout(() => {
      restoreVolume();
      showSnackbar("Waiting music restored.");
    }, TEMPORARY_MUTE_MS);
    showSnackbar("Waiting music muted for 30 seconds.");
  };

  const handleSignOut = async () => {
    setSignOutOpen(false);
    try {
      await updateAgentActivityState?.("offline")?.catch?.(() => {});
      await auth.signOut();
      showSnackbar("Signed out.", "warning");
    } catch (_) {
      showSnackbar("Could not sign out. Please try again.", "error");
    }
  };

  const openSettings = () => {
    setDraftAudioBitrate(controls.callAudioBitrateKbps);
    setSettingsOpen(true);
  };

  const saveSettings = () => {
    const bitrate = setAgentCallAudioBitrateKbps(draftAudioBitrate);
    setSettingsOpen(false);
    showSnackbar(
        bitrate === DATA_SAVER_BITRATE_KBPS ?
          "Data Saver will be used for your next call." :
          "High Fidelity will be used for your next call.",
    );
  };

  const sliderStyles = {
    color: "primary.main",
    "& .MuiSlider-rail": {
      opacity: 1,
      backgroundColor: themeMode === "dark" ? "rgba(115, 190, 168, 0.26)" : "rgba(0, 95, 80, 0.18)",
    },
    "& .MuiSlider-track": {border: "none"},
    "& .MuiSlider-thumb": {
      width: 18,
      height: 18,
      boxShadow: "0 2px 7px rgba(0, 61, 51, 0.22)",
      "&:before": {boxShadow: "none"},
    },
  };

  const muteTitle = muted || value === 0 ? "Restore waiting music" : "Mute waiting music for 30 seconds";
  const themeTitle = themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode";

  return (
    <Box
      component="aside"
      aria-label="Agent preferences"
      sx={{
        flex: "0 0 auto",
        width: compactLayout ? "100%" : 88,
        px: {xs: 1.5, md: 0},
        pl: {md: 2},
        pt: {xs: 1.2, md: 2},
        display: "flex",
        justifyContent: {xs: "center", md: "flex-start"},
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: compactLayout ? "min(100%, 520px)" : 72,
          height: compactLayout ? 64 : 300,
          minHeight: compactLayout ? 64 : 300,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        {compactLayout ? (
          <Stack direction="row" alignItems="center" sx={{height: "100%", px: 0.8}} spacing={0.5}>
            <Tooltip title={muteTitle}>
              <IconButton
                onClick={handleMuteToggle}
                color={muted || value === 0 ? "warning" : "primary"}
                aria-label={muteTitle}
                sx={{width: 40, height: 40}}
              >
                <VolumeIcon value={value} />
              </IconButton>
            </Tooltip>
            <Slider
              aria-label="Waiting music volume"
              value={value}
              onChange={handleChange}
              valueLabelDisplay="auto"
              sx={{...sliderStyles, flex: 1, mx: 0.8}}
            />
            <Typography aria-live="polite" sx={{width: 34, textAlign: "right", color: "text.secondary", fontSize: "0.7rem", fontWeight: 600}}>
              {value}%
            </Typography>
            <Divider orientation="vertical" flexItem sx={{mx: 0.4, my: 1.25}} />
            <Tooltip title={themeTitle}>
              <IconButton onClick={toggleThemeMode} aria-label={themeTitle} sx={{width: 38, height: 38}}>
                {themeMode === "dark" ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Settings">
              <IconButton onClick={openSettings} aria-label="Settings" sx={{width: 38, height: 38}}>
                <SettingsRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton onClick={() => setSignOutOpen(true)} aria-label="Sign out" color="error" sx={{width: 38, height: 38}}>
                <LogoutRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Stack alignItems="center" sx={{height: "100%", py: 0.8}}>
            <Tooltip title={themeTitle} placement="right">
              <IconButton onClick={toggleThemeMode} aria-label={themeTitle} sx={{width: 38, height: 38}}>
                {themeMode === "dark" ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Settings" placement="right">
              <IconButton onClick={openSettings} aria-label="Settings" sx={{width: 38, height: 38}}>
                <SettingsRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Divider flexItem sx={{mx: 1.2, my: 0.35}} />

            <Typography aria-live="polite" sx={{mt: 0.25, color: "text.secondary", fontSize: "0.66rem", fontWeight: 600}}>
              {value}%
            </Typography>
            <Slider
              orientation="vertical"
              aria-label="Waiting music volume"
              value={value}
              onChange={handleChange}
              sx={{...sliderStyles, height: 82, mt: 0.25, mb: 0.25}}
            />
            <Tooltip title={muteTitle} placement="right">
              <IconButton
                onClick={handleMuteToggle}
                color={muted || value === 0 ? "warning" : "primary"}
                aria-label={muteTitle}
                sx={{width: 38, height: 38}}
              >
                <VolumeIcon value={value} fontSize="small" />
              </IconButton>
            </Tooltip>

            <Box sx={{flex: 1}} />
            <Typography sx={{color: "text.disabled", fontSize: "0.58rem", lineHeight: 1, mb: 0.5}}>
              v1.50
            </Typography>
            <Tooltip title="Sign out" placement="right">
              <IconButton onClick={() => setSignOutOpen(true)} aria-label="Sign out" color="error" sx={{width: 38, height: 38}}>
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Paper>

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agent settings</DialogTitle>
        <DialogContent>
          <Typography variant="overline" color="text.secondary">
            Call data and quality
          </Typography>
          <RadioGroup
            aria-label="Call audio quality"
            value={String(draftAudioBitrate)}
            onChange={(event) => setDraftAudioBitrate(Number(event.target.value))}
            sx={{mt: 1, gap: 1.25}}
          >
            <Paper variant="outlined" sx={{p: 1.5, borderColor: draftAudioBitrate === DATA_SAVER_BITRATE_KBPS ? "primary.main" : "divider"}}>
              <FormControlLabel
                value={String(DATA_SAVER_BITRATE_KBPS)}
                control={<Radio />}
                sx={{alignItems: "flex-start", m: 0, width: "100%"}}
                label={
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{pt: 0.4}}>
                    <DataSaverOnRoundedIcon color="primary" />
                    <Box>
                      <Typography fontWeight={700}>Data Saver · 24 kbps</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Clear speech with approximately 40–50% less call data when both sides use it. Recommended.
                      </Typography>
                    </Box>
                  </Stack>
                }
              />
            </Paper>
            <Paper variant="outlined" sx={{p: 1.5, borderColor: draftAudioBitrate === HIGH_FIDELITY_BITRATE_KBPS ? "primary.main" : "divider"}}>
              <FormControlLabel
                value={String(HIGH_FIDELITY_BITRATE_KBPS)}
                control={<Radio />}
                sx={{alignItems: "flex-start", m: 0, width: "100%"}}
                label={
                  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{pt: 0.4}}>
                    <GraphicEqRoundedIcon color="action" />
                    <Box>
                      <Typography fontWeight={700}>High Fidelity · 48 kbps</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Richer audio with higher data use.
                      </Typography>
                    </Box>
                  </Stack>
                }
              />
            </Paper>
          </RadioGroup>
          <Alert severity="info" sx={{mt: 2}}>
            This choice is saved on this device and applies to both sides of your next call.
            {callActive ? " Your current call will not change." : ""}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={saveSettings} variant="contained">Save settings</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={signOutOpen} onClose={() => setSignOutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign out of Frecall?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            You will stop receiving callers until you sign in again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignOutOpen(false)} color="inherit">Stay signed in</Button>
          <Button onClick={handleSignOut} color="error" variant="contained">Sign out</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(AudioControls);
